import React, { useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../firebase";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import { Field, Fieldset, FieldGroup, Label } from "../../../design/fieldset";
import { Input } from "../../../design/input";
import { Select } from "../../../design/select";
import { Checkbox, CheckboxField } from "../../../design/checkbox";
import { useAccount } from "../../../contexts/AccountContext";
import { Visit } from "@visitproof/types";
import { LinkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";

interface RequestPaymentButtonProps {
  visit: Visit;
}

/**
 * Generates a Stripe Checkout link a parent can use to pay for this visit —
 * usable before the visit happens. Only shown when the account has online
 * payments enabled (Settings → Payments).
 */
const RequestPaymentButton: React.FC<RequestPaymentButtonProps> = ({ visit }) => {
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [parentId, setParentId] = useState("");
  const [amount, setAmount] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [emailResult, setEmailResult] = useState<{ sent: boolean; error?: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-entrancy latch: guards against a fast double-click firing two
  // createVisitCheckoutSession calls before isSubmitting re-renders.
  const inFlight = useRef(false);

  const paymentsEnabled = account?.settings?.features?.enableStripePayments;
  const parents = Object.entries(visit.parents || {});

  if (!paymentsEnabled || visit.paymentStatus === "paid") return null;

  const handleSubmit = async () => {
    if (inFlight.current) return;

    const amountCents = Math.round(parseFloat(amount) * 100);
    if (!parentId || !amountCents || amountCents < 100) {
      setError("Select a parent and enter an amount of at least $1.00.");
      return;
    }

    inFlight.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      const createVisitCheckoutSession = httpsCallable(
        functions,
        "createVisitCheckoutSession",
      );
      const { data } = (await createVisitCheckoutSession({
        visitId: visit.id,
        parentId,
        amountCents,
        sendEmail,
      })) as { data: { url: string; emailSent: boolean; emailError?: string } };
      setPaymentUrl(data.url);
      setEmailResult({ sent: data.emailSent, error: data.emailError });
    } catch (e: any) {
      console.error("Error creating payment link:", e);
      setError(e?.message || "Failed to create payment link.");
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!paymentUrl) return;
    await navigator.clipboard.writeText(paymentUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setIsOpen(false);
    setPaymentUrl(null);
    setEmailResult(null);
    setError(null);
    setCopied(false);
  };

  return (
    <>
      <Button outline onClick={() => setIsOpen(true)}>
        <LinkIcon className="w-4 h-4" />
        Request Payment
      </Button>

      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>Request Payment</DialogTitle>
        {paymentUrl ? (
          <>
            <DialogDescription>
              {emailResult?.sent
                ? "Payment link created and emailed to the parent. The visit is marked paid automatically once they complete payment."
                : "Payment link created. Send it to the parent — the visit is marked paid automatically once they complete payment."}
            </DialogDescription>
            <DialogBody>
              {emailResult?.error && (
                <p className="mb-3 text-sm text-amber-600">{emailResult.error}</p>
              )}
              <div className="flex items-center gap-2">
                <Input readOnly value={paymentUrl} onFocus={(e) => e.target.select()} />
                <Button onClick={handleCopy}>
                  {copied ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4" /> Copied
                    </>
                  ) : (
                    "Copy"
                  )}
                </Button>
              </div>
            </DialogBody>
            <DialogActions>
              <Button plain onClick={handleClose}>
                Done
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogDescription>
              Create a secure Stripe payment link for this visit. You can
              request payment before the visit takes place.
            </DialogDescription>
            <DialogBody>
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label>Parent</Label>
                    <Select value={parentId} onChange={(e) => setParentId(e.target.value)}>
                      <option value="">Select a parent…</option>
                      {parents.map(([id, p]) => (
                        <option key={id} value={id}>
                          {p.firstName} {p.lastName}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    <Label>Amount (USD)</Label>
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      placeholder="e.g. 75.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </Field>
                  <CheckboxField>
                    <Checkbox checked={sendEmail} onChange={setSendEmail} />
                    <Label>Email the payment link to the parent</Label>
                  </CheckboxField>
                </FieldGroup>
              </Fieldset>
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            </DialogBody>
            <DialogActions>
              <Button plain onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Creating…" : "Create Payment Link"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default RequestPaymentButton;
